import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { BreadcrumbService } from './breadcrumb.service';
import { SidebarMenuStateService } from './sidebarmenustateservice';
import { menuMetaConfig, SUBMENU_ID } from '../classes/domain/constants';

@Injectable({ providedIn: 'root' })
export class BreadcrumbAutoService {

  constructor(
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private sidebarMenuState: SidebarMenuStateService
  ) { }

  init() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.buildBreadcrumb(e.urlAfterRedirects);
      });
    this.buildBreadcrumb(this.router.url);
  }

  private buildBreadcrumb(url: string) {
    const cleanUrl = url
      .replace('/#/', '')
      .replace('#/', '')
      .replace(/^\//, '');

    const pathOnly = cleanUrl.split('?')[0].split('#')[0];

    // ── CASE 1: Home / Dashboard ──────────────
    if (pathOnly === '' || pathOnly.includes(SUBMENU_ID.HOME) || pathOnly === SUBMENU_ID.UPDATED_DASHBOARD || pathOnly === SUBMENU_ID.DASHBOARD || pathOnly === SUBMENU_ID.OLD_DASHBOARD) {
      this.breadcrumbService.set([{ label: 'Home', labelKey: 'MENU.Dashboard' }]);
      return;
    }

    // ── CASE 1.1: Today's Tasks List ──────────
    if (pathOnly === SUBMENU_ID.TODAYS_TASK_LIST) {
      this.breadcrumbService.set([
        { label: 'Home', labelKey: 'MENU.Dashboard', path: SUBMENU_ID.HOME },
        { label: "Today's Tasks", labelKey: 'TODAYS_TASK.ALL_TASKS_TODAY' },
      ]);
      return;
    }

    // ── CASE 1.2: Today's Tasks Details ───────
    if (pathOnly === SUBMENU_ID.TODAYS_TASK_LIST_DETAILS) {
      let taskTitle = '';
      try {
        const nav = history.state?.title || JSON.parse(sessionStorage.getItem('todaysTaskDetailsNav') || '{}')?.title;
        taskTitle = nav || '';
      } catch { }

      this.breadcrumbService.set([
        { label: 'Home', labelKey: 'MENU.Dashboard', path: SUBMENU_ID.HOME },
        { label: "Today's Tasks", labelKey: 'TODAYS_TASK.ALL_TASKS_TODAY', path: SUBMENU_ID.TODAYS_TASK_LIST },
        { label: taskTitle ? `${taskTitle} Details` : 'Task Details' },
      ]);
      return;
    }

    // ── CASE 2: Feature Group ─────────────────
    const featureMatch = cleanUrl.match(/Feature-Group\/([^?#]+)/);
    if (featureMatch) {
      const feature = decodeURIComponent(featureMatch[1] || '');
      this.breadcrumbService.set([
        { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
        { label: feature, labelKey: `FEATURE.${feature}` }
      ]);
      return;
    }

    // ── CASE 5: Dashboard pages ───────────────
    const dashboardPaths = [
      'Dashboard_Anemia_Mukta_Bharat',
      'Dashboard_Asha_Incentive',
      'Dashboard_Contraceptive_pills',
      'Asha-Af-Employee-State-Incentive-Report',
      'dashboard-drilldown-report',
    ];
    if (dashboardPaths.includes(pathOnly)) {
      return;
    }

    // ── CASE 5.1: Pages that set their own breadcrumb in the component ──
    const componentLevelPaths: string[] = [
      SUBMENU_ID.ANC_REGISTER_FOLLOW_UP_DETAILS,
      SUBMENU_ID.CHANGE_HEAD_OF_FAMILY,
      SUBMENU_ID.SPLIT_FAMILY_DETAILS,
    ];
    if (componentLevelPaths.includes(pathOnly)) {
      return;
    }

    // ── CASE 6: MAA Meeting immediate breadcrumb ────────

    // ── CASE 6: MAA Meeting immediate breadcrumb ────────
    if (pathOnly === SUBMENU_ID.MAA_MEETING) {
      this.breadcrumbService.set([
        { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
        { label: 'Transaction', labelKey: 'FEATURE.Transaction', path: 'Feature-Group/Transaction' },
        { label: 'Mother', labelKey: 'FEATURE_GROUP.Mother', path: 'Feature-Group/Transaction' },
        { label: 'MAA Meeting', labelKey: 'MENU.MAA_Meeting' }
      ]);
    } else if (pathOnly === SUBMENU_ID.MAA_MEETING_DETAILS) {
      this.breadcrumbService.set([
        { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
        { label: 'Transaction', labelKey: 'FEATURE.Transaction', path: 'Feature-Group/Transaction' },
        { label: 'Mother', labelKey: 'FEATURE_GROUP.Mother', path: 'Feature-Group/Transaction' },
        { label: 'MAA Meeting', labelKey: 'MENU.MAA_Meeting', path: 'MAA_Meeting' },
        { label: 'MAA Meeting Details', labelKey: 'MENU.MAA_Meeting_Details' }
      ]);
    }

    // ── CASES 3 & 4: Need menu data ───────────
    //  wait until menu has real data (fixes refresh issue)
    this.sidebarMenuState.menu$
      .pipe(
        filter(menu => menu.length > 0),  //  skip empty array on refresh
        take(1)
      )
      .subscribe(menu => {
        this.buildBreadcrumbWithMenu(pathOnly, menu);
      });
  }

  private buildBreadcrumbWithMenu(cleanUrl: string, menu: any[]) {

    // ── CASE 4: Menu page ──────────────────────
    const matchedEntry = Object.entries(menuMetaConfig).find(
      ([_, val]) => val.path === cleanUrl
    );

    if (matchedEntry) {
      const [menuId, meta] = matchedEntry;
      const menuItem = menu.find(m => m.MenuId === menuId || (menuId === 'MAA_Meeting' && m.MenuId === 'Mother_Meeting') || (menuId === 'Mother_Meeting' && m.MenuId === 'MAA_Meeting'));

      if (menuItem) {
        // ✅ normalize FeatureGroup key — spaces → underscores
        const featureGroupKey = menuItem.FeatureGroup.replaceAll(' ', '_');

        this.breadcrumbService.set([
          { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
          { label: menuItem.Feature, labelKey: `FEATURE.${menuItem.Feature}`, path: `Feature-Group/${menuItem.Feature}` },
          { label: menuItem.FeatureGroup.replaceAll('_', ' '), labelKey: `FEATURE_GROUP.${featureGroupKey}`, path: `Feature-Group/${menuItem.Feature}` },
          { label: meta.text, labelKey: `MENU.${menuId}` },
        ]);
        this.breadcrumbService.setExpandGroup(menuItem.FeatureGroup);
      } else {
        this.breadcrumbService.set([
          { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
          { label: meta.text, labelKey: `MENU.${menuId}`, path: meta.path },
        ]);
      }
      return;
    }

    // ── CASE 5: Detail page ────────────────────
    // ⚠️ REPLACE the existing detailSuffixes block in buildBreadcrumbWithMenu
    const detailSuffixes = ['_add_details', '_edit_details', '_form_details', '_details', '_detail']
      .sort((a, b) => b.length - a.length); // longest suffix wins

    let parentUrl = cleanUrl;
    let detailType = 'Details';   // Details | Add | Edit

    const lowerCleanUrl = cleanUrl.toLowerCase();

    for (const suffix of detailSuffixes) {
      if (lowerCleanUrl.endsWith(suffix)) {
        parentUrl = cleanUrl.slice(0, cleanUrl.length - suffix.length);
        if (suffix.includes('add')) detailType = 'Add';
        else if (suffix.includes('edit')) detailType = 'Edit';
        break;
      }
    }

    const parentEntry = Object.entries(menuMetaConfig).find(
      ([_, val]) => val.path.toLowerCase() === parentUrl.toLowerCase()
    );

    if (parentEntry) {
      const [menuId, meta] = parentEntry;
      const menuItem = menu.find(m => m.MenuId === menuId || (menuId === 'MAA_Meeting' && m.MenuId === 'Mother_Meeting') || (menuId === 'Mother_Meeting' && m.MenuId === 'MAA_Meeting'));

      if (menuItem) {
        // ✅ normalize FeatureGroup key — spaces → underscores
        const featureGroupKey = menuItem.FeatureGroup.replaceAll(' ', '_');

        this.breadcrumbService.set([
          { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
          { label: menuItem.Feature, labelKey: `FEATURE.${menuItem.Feature}`, path: `Feature-Group/${menuItem.Feature}` },
          { label: menuItem.FeatureGroup.replaceAll('_', ' '), labelKey: `FEATURE_GROUP.${featureGroupKey}`, path: `Feature-Group/${menuItem.Feature}` },
          { label: meta.text, labelKey: `MENU.${menuId}`, path: meta.path },
          { label: `${meta.text} ${detailType}`, labelKey: `MENU.${menuId}_${detailType}` },
        ]);
        this.breadcrumbService.setExpandGroup(menuItem.FeatureGroup);
      } else {
        this.breadcrumbService.set([
          { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' },
          { label: meta.text, labelKey: `MENU.${menuId}`, path: meta.path },
          { label: `${meta.text} ${detailType}`, labelKey: `MENU.${menuId}_${detailType}` },
        ]);
      }
      return;
    }

    // ── Fallback ───────────────────────────────
    this.breadcrumbService.set([
      { label: 'Home', labelKey: 'MENU.Dashboard', path: 'home' }
    ]);
  }
}